import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@werkstatt/ui';
import { getCsrfToken } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Mail, Eye, EyeOff, Send } from 'lucide-react';

interface EmailSettings {
  email_host: string | null;
  email_port: number | null;
  email_security: string | null;
  email_user: string | null;
  email_from_name: string | null;
  email_from: string | null;
  email_reply_to: string | null;
  email_bcc: string | null;
  email_signature: string | null;
  has_password: boolean;
}

interface EmailSettingsForm {
  email_host: string;
  email_port: string;
  email_security: string;
  email_user: string;
  email_password: string;
  email_from_name: string;
  email_from: string;
  email_reply_to: string;
  email_bcc: string;
  email_signature: string;
}

const defaultForm: EmailSettingsForm = {
  email_host: '',
  email_port: '587',
  email_security: 'STARTTLS',
  email_user: '',
  email_password: '',
  email_from_name: '',
  email_from: '',
  email_reply_to: '',
  email_bcc: '',
  email_signature: '',
};

export default function EmailSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<EmailSettingsForm>(defaultForm);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['email-settings'],
    queryFn: async () => {
      const response = await fetch('/api/settings/email', { credentials: 'include' });
      if (!response.ok) throw new Error('Fehler beim Laden der E-Mail-Einstellungen');
      return response.json() as Promise<EmailSettings>;
    },
  });

  useEffect(() => {
    if (settings) {
      setForm({
        email_host: settings.email_host ?? '',
        email_port: settings.email_port ? String(settings.email_port) : '587',
        email_security: settings.email_security ?? 'STARTTLS',
        email_user: settings.email_user ?? '',
        email_password: '',
        email_from_name: settings.email_from_name ?? '',
        email_from: settings.email_from ?? '',
        email_reply_to: settings.email_reply_to ?? '',
        email_bcc: settings.email_bcc ?? '',
        email_signature: settings.email_signature ?? '',
      });
      setPasswordChanged(false);
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data: EmailSettingsForm) => {
      const payload: Record<string, unknown> = {
        email_host: data.email_host || null,
        email_port: data.email_port ? parseInt(data.email_port, 10) : null,
        email_security: data.email_security,
        email_user: data.email_user || null,
        email_from_name: data.email_from_name || null,
        email_from: data.email_from || null,
        email_reply_to: data.email_reply_to || null,
        email_bcc: data.email_bcc || null,
        email_signature: data.email_signature || null,
      };
      if (passwordChanged && data.email_password) {
        payload.email_password = data.email_password;
      }
      const response = await fetch('/api/settings/email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Speichern fehlgeschlagen');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-settings'] });
      setPasswordChanged(false);
      setForm(prev => ({ ...prev, email_password: '' }));
      toast({ title: 'E-Mail-Einstellungen gespeichert' });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: 'Fehler', description: error.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (recipient: string) => {
      const response = await fetch('/api/settings/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify({ recipient }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Testmail fehlgeschlagen');
      }
      return response.json() as Promise<{ success: boolean; message: string }>;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: 'Testmail gesendet', description: result.message });
      } else {
        toast({ variant: 'destructive', title: 'Testmail fehlgeschlagen', description: result.message });
      }
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: 'Fehler', description: error.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Mail className="h-5 w-5" />
            SMTP-Konfiguration
          </CardTitle>
          <CardDescription>
            Konfigurieren Sie den ausgehenden E-Mail-Server für automatische Benachrichtigungen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Server */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <Label htmlFor="email_host">SMTP-Server</Label>
                <Input
                  id="email_host"
                  value={form.email_host}
                  onChange={e => setForm({ ...form, email_host: e.target.value })}
                  placeholder="smtp.example.com"
                />
              </div>
              <div>
                <Label htmlFor="email_port">Port</Label>
                <Input
                  id="email_port"
                  type="number"
                  value={form.email_port}
                  onChange={e => setForm({ ...form, email_port: e.target.value })}
                  placeholder="587"
                />
              </div>
            </div>

            <div>
              <Label>Verschlüsselung</Label>
              <Select
                value={form.email_security}
                onValueChange={v => setForm({ ...form, email_security: v })}
              >
                <SelectTrigger className="mt-1.5 w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Keine</SelectItem>
                  <SelectItem value="STARTTLS">STARTTLS (empfohlen)</SelectItem>
                  <SelectItem value="SSL">SSL/TLS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Zugangsdaten */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="email_user">Benutzername</Label>
                <Input
                  id="email_user"
                  value={form.email_user}
                  onChange={e => setForm({ ...form, email_user: e.target.value })}
                  placeholder="user@example.com"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="email_password">
                  Passwort{settings?.has_password && ' (gesetzt — leer lassen um beizubehalten)'}
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="email_password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.email_password}
                    onChange={e => {
                      setForm({ ...form, email_password: e.target.value });
                      setPasswordChanged(true);
                    }}
                    placeholder={settings?.has_password ? '••••••••' : 'Passwort eingeben'}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Absender */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="email_from_name">Absender-Name</Label>
                <Input
                  id="email_from_name"
                  value={form.email_from_name}
                  onChange={e => setForm({ ...form, email_from_name: e.target.value })}
                  placeholder="RentalHub GmbH"
                />
              </div>
              <div>
                <Label htmlFor="email_from">Absender-Adresse</Label>
                <Input
                  id="email_from"
                  type="email"
                  value={form.email_from}
                  onChange={e => setForm({ ...form, email_from: e.target.value })}
                  placeholder="noreply@example.com"
                />
              </div>
            </div>

            {/* Optional */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="email_reply_to">Reply-To (optional)</Label>
                <Input
                  id="email_reply_to"
                  type="email"
                  value={form.email_reply_to}
                  onChange={e => setForm({ ...form, email_reply_to: e.target.value })}
                  placeholder="service@example.com"
                />
              </div>
              <div>
                <Label htmlFor="email_bcc">BCC (optional, kommagetrennt)</Label>
                <Input
                  id="email_bcc"
                  value={form.email_bcc}
                  onChange={e => setForm({ ...form, email_bcc: e.target.value })}
                  placeholder="leitung@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email_signature">Signatur</Label>
              <Textarea
                id="email_signature"
                value={form.email_signature}
                onChange={e => setForm({ ...form, email_signature: e.target.value })}
                rows={3}
                placeholder={'Mit freundlichen Grüßen\nIhre Autovermietung'}
                className="mt-1.5"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Wird im Postfach automatisch ans Ende einer neuen Antwort gestellt —
                lässt sich dort vor dem Senden noch bearbeiten oder entfernen.
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Einstellungen speichern
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Send className="h-5 w-5" />
            Testmail
          </CardTitle>
          <CardDescription>
            Prüft die gespeicherte Konfiguration mit einem echten Versand.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="test_recipient">Empfänger</Label>
              <Input
                id="test_recipient"
                type="email"
                value={testRecipient}
                onChange={e => setTestRecipient(e.target.value)}
                placeholder="ich@example.com"
                className="mt-1.5"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!testRecipient || testMutation.isPending}
              onClick={() => testMutation.mutate(testRecipient)}
            >
              {testMutation.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Send className="mr-2 h-4 w-4" />}
              Testmail senden
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display">Typische Konfigurationen</CardTitle>
          <CardDescription>Schnellreferenz für gängige E-Mail-Anbieter</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm md:grid-cols-2 lg:grid-cols-3">
            {[
              { name: 'Gmail', host: 'smtp.gmail.com', port: 587, sec: 'STARTTLS' },
              { name: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587, sec: 'STARTTLS' },
              { name: 'IONOS', host: 'smtp.ionos.de', port: 587, sec: 'STARTTLS' },
              { name: 'Strato', host: 'smtp.strato.de', port: 465, sec: 'SSL' },
              { name: 'Postmark', host: 'smtp.postmarkapp.com', port: 587, sec: 'STARTTLS' },
              { name: 'Mailgun', host: 'smtp.mailgun.org', port: 587, sec: 'STARTTLS' },
            ].map(p => (
              <button
                key={p.name}
                type="button"
                onClick={() => setForm(f => ({
                  ...f,
                  email_host: p.host,
                  email_port: String(p.port),
                  email_security: p.sec,
                }))}
                className="rounded-lg border p-3 text-left hover:bg-muted transition-colors"
              >
                <div className="font-medium">{p.name}</div>
                <div className="text-muted-foreground">{p.host}:{p.port} · {p.sec}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
